import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { makeApi } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordField, DRIVER_PW_MIN } from "./PasswordField";
import { EMPTY_PROFILE, ProfileFields, profileValid, profileBody } from "./ProfileFields";

const EMPTY = { ...EMPTY_PROFILE, username: "", password: "" };
export const USERNAME_OK = /^[a-z0-9._-]{3,40}$/;

// Live check against every driver in the whole system; reports "checking" | "ok" | "taken" | "invalid".
export const UsernameField = ({ value, onChange, excludeId, onStatus = () => {}, original = "" }) => {
  const { getToken } = useAuth();
  const [status, setStatus] = useState("invalid");
  useEffect(() => {
    const report = (s) => { setStatus(s); onStatus(s); };
    if (!USERNAME_OK.test(value)) { report("invalid"); return undefined; }
    if (value === original) { report("ok"); return undefined; }
    report("checking");
    const t = setTimeout(async () => {
      try {
        const { data } = await makeApi(getToken).get("/admin/drivers/username-check", { params: { username: value, exclude: excludeId || undefined } });
        report(data.available ? "ok" : "taken");
      } catch { report("ok"); /* save re-checks */ }
    }, 350);
    return () => clearTimeout(t);
  }, [value, excludeId, original]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <Label htmlFor="driver-username">Username *</Label>
      <div className="mt-1.5 relative">
        <Input id="driver-username" className={`h-11 font-mono pr-9 ${status === "taken" ? "border-rose-400" : ""}`} value={value} autoCapitalize="none" spellCheck={false}
          onChange={(e) => onChange(e.target.value.toLowerCase().replace(/\s/g, ""))} placeholder="e.g. mike.j" data-testid="driver-username-input" />
        <span className="absolute right-3 top-3.5">
          {status === "checking" && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          {status === "ok" && value && <CheckCircle2 className="w-4 h-4 text-emerald-600" data-testid="driver-username-ok" />}
        </span>
      </div>
      {status === "taken"
        ? <p className="mt-1.5 text-xs font-medium text-rose-600" data-testid="driver-username-taken">Username taken — try another</p>
        : <p className="mt-1.5 text-xs text-slate-500">3–40 letters, numbers, dots, dashes or underscores. Must be unique across all of Fleet Sheet.</p>}
    </div>
  );
};

export const AddDriverDialog = ({ open, onClose, onSubmit }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [userStatus, setUserStatus] = useState("invalid");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const close = () => { setForm(EMPTY); setError(""); onClose(); };
  const valid = profileValid(form) && userStatus === "ok" && form.password.length >= DRIVER_PW_MIN;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const err = await onSubmit({ ...profileBody(form), username: form.username, password: form.password });
    setBusy(false);
    if (err) setError(err);
    else setForm(EMPTY);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-2xl" data-testid="add-driver-dialog">
        <DialogHeader>
          <DialogTitle>Add driver</DialogTitle>
          <DialogDescription>You set their username and password. They sign in at the driver login with these — no signup or email needed.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-6 max-h-[70vh] overflow-y-auto pr-1">
          <fieldset className="space-y-3">
            <legend className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Login</legend>
            <div className="grid gap-4">
              <UsernameField value={form.username} onChange={(v) => set("username", v)} onStatus={setUserStatus} />
              <PasswordField value={form.password} onChange={(v) => set("password", v)} label="Password *" min={DRIVER_PW_MIN} />
            </div>
          </fieldset>
          <ProfileFields f={form} set={set} />
          {error && <p className="text-sm text-rose-600" data-testid="add-driver-error">{error}</p>}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={close} data-testid="add-driver-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={!valid || busy} className="bg-[#002FA7] hover:bg-[#001A75]" data-testid="add-driver-submit-btn">
              {busy ? "Creating…" : "Create driver"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
